import styled from '@emotion/styled';
import { useRef, forwardRef } from 'react';
import { useButton, useSearchField } from 'react-aria';
import { useSearchFieldState } from 'react-stately';

import { mergeRefs } from '../../../lib/mergeRefs';
import { Glyph } from '../Glyph';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';
import glyphStyles from '../Glyph.module.css';

export const SearchField = forwardRef(function (props, forwardedRef) {
  const inputRef = useRef();
  const buttonRef = useRef();
  const searchFieldState = useSearchFieldState(props);
  const searchField = useSearchField(props, searchFieldState, inputRef);
  const button = useButton({ ...searchField.clearButtonProps, elementType: 'span' }, buttonRef);
  const variant = props.variant;

  return (
    <SearchField.Root className={props.className}>
      <SearchField.Relative>
        {props.label && <label {...searchField.labelProps}>{props.label}</label>}

        <SearchField.Input
          {...searchField.inputProps}
          ref={mergeRefs([inputRef, forwardedRef])}
          variant={variant}
          backgroundColor={props.backgroundColor}
          borderRadius={props.borderRadius}
        />

        <SearchField.IconFilterWrapper>
          <SearchField.IconFilter
            source={glyph.core.magnifyingglass}
            color={theme.icon.color_body_light}
            size={theme.icon.size_medium}
          />
        </SearchField.IconFilterWrapper>

        {searchFieldState.value !== '' && (
          <SearchField.IconClear {...button.buttonProps} ref={buttonRef}>
            <Glyph
              source={glyph.core.cross}
              color={theme.color.icon_body_light}
              size={theme.icon.size_default}
            />
          </SearchField.IconClear>
        )}
      </SearchField.Relative>

      {props.children}
    </SearchField.Root>
  );
});

SearchField.Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  //width: 240px;
`;

SearchField.Relative = styled.div`
  width: 100%;
  position: relative;
`;

SearchField.IconFilterWrapper = styled.div`
  position: absolute;
  inset-inline-start: 15px;
  top: 50%;
  transform: translate(0, -50%);
  transition: ${motion.transition.micro};
`;

SearchField.IconFilter = styled(Glyph)`
  pointer-events: none;
`;

SearchField.Input = styled.input`
  min-height: ${su(5)};
  width: 100%;
  outline: 0;
  border: 1px solid ${theme.input.search_border};
  line-height: 20px;
  padding-block: ${su(1)};
  padding-inline: ${su(6)} ${su(4)};
  border-radius: ${(props) => props.borderRadius ?? '20px'};
  transition: ${motion.transition.micro};
  background-color: ${(props) =>
    props.backgroundColor
      ? props.backgroundColor
      : props.variant === 'flat'
        ? 'transparent'
        : theme.input.search_background};
  color: ${theme.color.text};
  caret-color: ${theme.color.mono_1000};

  &::placeholder {
    color: ${theme.color.text_light};
  }

  &[value='']:hover:not(:focus) {
    background: ${(props) =>
      props.backgroundColor
        ? props.backgroundColor
        : props.variant === 'flat'
          ? 'transparent'
          : theme.color.component};
    border-color: ${theme.color.line};
    box-shadow: ${theme.input.box_shadow_hover};

    & + ${SearchField.IconFilter} {
      color: ${theme.color.icon_light_hover};
    }
  }

  &:focus {
    background: ${(props) =>
      props.backgroundColor
        ? props.backgroundColor
        : props.variant === 'flat'
          ? 'transparent'
          : theme.color.component};
    border-color: ${theme.input.border_focus};
    box-shadow: ${theme.input.box_shadow_focus};

    &::placeholder {
      //color: ${theme.color.mono_200};
    }

    & + ${SearchField.IconFilter} {
      color: ${theme.color.icon_light_hover};
    }
  }
`;

SearchField.IconClear = styled.div`
  position: absolute;
  inset-inline-end: ${su(1)};
  top: 50%;
  transform: translate(0, -50%);
  cursor: pointer;
  padding: 2px;

  .${glyphStyles.root} {
    transition: ${motion.transition.micro};
  }

  &:hover {
    .${glyphStyles.root} {
      color: ${theme.color.icon_light_hover};
      transform: scale(1.125);
    }
  }
`;
